/**
 * client/src/components/org-switcher/option-row.tsx
 *
 * Row presentation for one profile option in the switcher dropdown.
 */

import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Row styling for one profile option.
 *
 * The current profile previously carried `aria-selected` and NOTHING visual —
 * so an open dropdown showed Personal / <org> / Platform Console looking
 * byte-identical, and the one question the user opened it to answer ("which am
 * I on?") went unanswered. Selection is now marked THREE redundant ways —
 * weight, a leading accent rule, and an explicit ✓ + "Current" text — so it
 * never depends on colour alone (WCAG 1.4.1) and survives a colour-blind or
 * high-contrast viewer. Deliberately NOT emerald: per CLAUDE.md Design
 * Constraints that token means success / hardware-confirmed / AAL milestone,
 * and "this is the profile you're on" is a state, not an achievement.
 */
export function optionClass(selected: boolean): string {
  return cn(
    "flex w-full items-center gap-2 border-l-2 px-3 py-2 text-left text-sm",
    "transition-colors duration-[var(--duration-fast)]",
    selected
      ? "border-interactive bg-bg-elevated font-semibold text-text-primary"
      : "border-transparent text-text-primary hover:bg-bg-elevated",
  );
}

/**
 * ✓ + "Current" marker for the active row (see optionClass).
 *
 * `className` lets a caller place it: single-line rows (Personal, Platform
 * Console) push it to the trailing edge with `ml-auto`, while the two-line org
 * row leads its metadata line with it so the org NAME keeps the full width.
 */
export function CurrentMarker({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary",
        className,
      )}
    >
      <Check size={13} aria-hidden="true" />
      Current
    </span>
  );
}
